import Screen from "../lib/Screen";

import * as PIXI from "pixi.js";
import * as pp from "@pixi/particle-emitter";
import gsap from "gsap";
import { IGameSnacks } from "../lib/GameSnacks";

import Card, { card_color_type, card_value_type } from "./game/Card";
import Player, { game_side_type, ICardLayout } from "./game/Player";
import ServiceLocator from "../ServiceLocator";
import Utils from "../Utils";
import Session, { game_type } from "../Session";
import Input from "./Input";
import MouseInput from "./MouseInput";
import TouchInput from "./TouchInput";
import { LiveEventData, LivePlayerDataEvent, LivePresenceAction } from "../lib/game_snacks/ILiveMultiplayer";
import { screen_type } from "../state/GameState";
import ColorSelectionPopup from "./popup/ColorSelectionPopup";
import RoundOverPopup from "./popup/RoundOverPopup";
import Settings from "./Settings";
import TutorialPopup from "./popup/TutorialPopup";
import LeaveConfirmationPopup from "./popup/LeaveConfirmationPopup";
import { game_event_type } from "../service/MultiplayerEventManager";
import AutoplayNotification from "./game/AutoplayNotification";
import ComicBubble from "./game/ComicBubble";
import SkipAnimation from "./game/SkipAnimation";
import SpecialCardAnimation from "./game/SpecialCardAnimation";
import ReverseAnimation from "./game/ReverseAnimation";
import DrawTwoAnimation from "./game/DrawTwoAnimation";
import GameOverAnimation from "./game/GameOverAnimation";
import DeckEntryAnimation from "./game/DeckEntryAnimation";
import DeckCreationAnimation from "./game/DeckCreationAnimation";
import ShinyBandShader from "./game/ShinyBandShader";
import DrawCountNotification from "./game/DrawCountNotification";
import { Profile } from "../lib/game_snacks/IUser";
import PlayerLeftNotification from "./game/PlayerLeftNotification";
import MessagePopup from "./popup/MessagePopup";


declare global {
    interface Window { GameSnacks: IGameSnacks; }
}


export default class GameplayScreen extends Screen
{
    // #region Attributes //

    // private:

    private _input: Input = null;

    private _layers: IGameLayers = null;

    private _playAreaCenter: PIXI.Point = null;

    private _deckShadow: PIXI.Container = null;
    private _arrCard: Array<Card> = null;
    private _arrPlayedCard: Array<Card> = null;
    private _deckWrapper: PIXI.Container = null;
    private _arrDeckCard: Array<Card> = null;
    private _leadCard: Card = null;
    private _arrPlayer: Array<Player> = null;
    private _curPlayer: Player = null;
    private _myPlayer: Player = null;
    private _drawCardTriggerArea: PIXI.Graphics = null;
    private _playOrderArrow: PIXI.Sprite = null;
    private _playOrderArrowOverlay: PIXI.Sprite = null;
    private _drawStackNotification: DrawCountNotification = null;
    private _autoplayNotification: AutoplayNotification = null;
    private _comicBubble: ComicBubble = null;
    private _hand: PIXI.Sprite = null;
    private _tentacle: PIXI.AnimatedSprite = null;
    private _compass: PIXI.AnimatedSprite = null;
    
    //private _sparkEmitter: pp.Emitter = null;
    private _wildCardFrameAnimation: PIXI.AnimatedSprite = null;
    private _sparkAnimation: PIXI.AnimatedSprite = null;
    private _shinyBandShader: ShinyBandShader = null;

    private _specialCardAnimation: SpecialCardAnimation = null;
    private _gameOverAnimation: GameOverAnimation = null;
    private _deckCreationAnimation: DeckCreationAnimation = null;
    private _deckEntryAnimation: DeckEntryAnimation = null;

    private _leadCardColor: card_color_type = null;
    private _playOrder: number = null;
    private _startingDeckSize: number = null;

    private _colorSelectionPopup: ColorSelectionPopup = null;
    private _roundOverPopup: RoundOverPopup = null;

    private _matchMakingData: IMatchMakingGame = null;

    private _playCardTl: gsap.core.Timeline = null;
    private _drawCardTl: gsap.core.Timeline = null;
    private _actionTextTl: gsap.core.Timeline = null;
    private _drawAlertTl: gsap.core.Timeline = null;
    private _cardWiggleTl: gsap.core.Timeline = null;

    private _isNextAction: boolean = null;
    private _isPaused: boolean = null;

    private _settings: Settings = null;

    private _playerInactivityTimer: number = null;
    private _imAliveTimer: number = null;

    private _state: state_type = null;
    private _drawStack: number = null;
    private _arrDummyHandCard: Array<PIXI.Sprite> = null;

    private _arrMissingPlayerId: Array<number> = null;

    private _isKeepRoom: boolean = null;

    private _winCardCount: number = null;

    private readonly _kDeckShadowOffsetX: number = -30;
    private readonly _kLeadCardPos: PIXI.Point = new PIXI.Point( 412, 572 );

    // #endregion //


    public get playAreaCenter(): PIXI.Point { return this._playAreaCenter; }

    
    // #region Methods //

    // public:

    public constructor()
    {
        super();
    }

    public onEnter(): void
    {
        super.onEnter();

        Session.i.isGameStarted = true;

        this._playOrder = 1;
        this._isNextAction = false;
        this._isPaused = false;
        this._state = state_type.PLAYING;
        this._drawStack = 1;
        this._arrDummyHandCard = [];
        this._arrMissingPlayerId = [];
        this._isKeepRoom = false;
        this._winCardCount = 0;
        //this._winCardCount = 6; // TEST:
        const kUrlParams = new URLSearchParams( window.location.search );
        if ( kUrlParams.has( "wcc" ) )
        {
            this._winCardCount = parseInt( kUrlParams.get( "wcc" ) );
        }

        if ( Session.i.gameType == game_type.MATCHMAKING )
        {
            this._matchMakingData = {
                round: 1,
                arrRoundScore: [ 0, 0, 0, 0 ],
                arrTotalScore: [ 0, 0, 0, 0 ] };
        }

        this._layers = {
            bg: new PIXI.Container(),
            shadow: new PIXI.Container(),
            player: new PIXI.Container(),
            deck: new PIXI.Container(),
            card: new PIXI.Container(),
            vfx: new PIXI.Container(),
            hud: new PIXI.Container(),
            top: new PIXI.Container(),
            inputDisabled: new PIXI.Container() };
        //
        this._root.addChild( this._layers.bg );
        this._root.addChild( this._layers.shadow );
        this._root.addChild( this._layers.player );
        this._root.addChild( this._layers.deck );
        this._root.addChild( this._layers.card );
        this._root.addChild( this._layers.vfx );
        this._root.addChild( this._layers.hud );
        this._root.addChild( this._layers.top );
        this._root.addChild( this._layers.inputDisabled );

        this._settings = new Settings();
        this._settings.init();
        this._settings.position.set( ServiceLocator.app.width - 58, 58 );
        this._root.addChild( this._settings );
        this._settings.onTutorial.add( () => { 
            let tutorialPopup: TutorialPopup = new TutorialPopup();
            tutorialPopup.init();
            this._root.addChild( tutorialPopup );
            tutorialPopup.onClosed.add( ( popup: TutorialPopup ) => {
                popup.end();

                this._input.setEnabled( true );
            } );

            this._input.setEnabled( false );
        } );
        this._settings.onHome.add( () => {
            let leaveConfirmationPopup: LeaveConfirmationPopup = new LeaveConfirmationPopup();
            leaveConfirmationPopup.onEnter();
            leaveConfirmationPopup.onClosed.add( ( popup: LeaveConfirmationPopup, isAccept: boolean ) => {
                popup.onLeave();
                if ( isAccept )
                {
                    window.GameSnacks.game.gameOver();

                    this._onCompleted.dispatch( screen_type.LOBBY );
                }
                else
                {
                    this._input.setEnabled( true );
                }
            } );

            this._input.setEnabled( false );
        } );
        //
        const kSettingsScale: number = 100 / this._settings.width;
        this._settings.position.x -= 58 * ( kSettingsScale - 1 ); 
        this._settings.position.y += 58 * ( kSettingsScale - 1 ) - 4; 
        this._settings.scale.set( kSettingsScale );

        if ( ServiceLocator.app.isTouchDevice )
        {
            this._input = new TouchInput();
        }
        else
        {
            this._input = new MouseInput();
        }
        this._input.layers = this._layers;
        this._input.init();

        let bg: PIXI.Sprite = new PIXI.Sprite( ServiceLocator.resources.game_bg.texture );
        bg.anchor.set( 0.5 );
        bg.position.set( ServiceLocator.app.width * 0.5, ServiceLocator.app.height * 0.5 );
        this._layers.bg.addChild( bg );
        //
        let spotlight: PIXI.Sprite = new PIXI.Sprite( ServiceLocator.resources.spotlight.texture );
        spotlight.anchor.set( 0.5 );
        spotlight.position.set( ServiceLocator.app.width * 0.5, ServiceLocator.app.height * 0.5 );
        this._layers.bg.addChild( spotlight );
        //
        this._tentacle = new PIXI.AnimatedSprite( Utils.createTexArray( "tentacle_in_anim_", 19 ) );
        this._tentacle.visible = false;
        this._tentacle.loop = false;
        this._tentacle.autoUpdate = false;
        this._tentacle.animationSpeed = 20;
        this._tentacle.scale.set( 0.8 );
        this._tentacle.anchor.set( 97/660, 333/660 );
        //this._tentacle.position.set( -ServiceLocator.app.safeAreaMarginX - 300, -ServiceLocator.app.safeAreaMarginY * 0.56 );
        this._tentacle.position.set( -ServiceLocator.app.safeAreaMarginX - 200, 0 );
        //this._tentacle.position.set( ServiceLocator.app.width * 0.5, ServiceLocator.app.height * 0.5 );
        this._layers.bg.addChild( this._tentacle );
        //this._layers.vfx.addChild( this._tentacle );
        this._tentacle.onComplete = () => {
            this._tentacle.animationSpeed = 10;
            this._tentacle.loop = true;
            this._tentacle.textures = Utils.createTexArray( "tentacle_idle_anim_", 35 );
            this._tentacle.gotoAndPlay( 0 );

            this._tentacle.onComplete = null;
        };
        //
        this._compass = new PIXI.AnimatedSprite( Utils.createTexArray( "compass_idle_anim_", 12 ) );
        this._compass.visible = false;
        this._compass.loop = false;
        this._compass.autoUpdate = false;
        this._compass.scale.set( 0.8 );
        this._compass.animationSpeed = 20;
        this._compass.anchor.set( 329/660, 268/660 );
        this._compass.position.set( ServiceLocator.app.width - 40, ServiceLocator.app.height + ServiceLocator.app.safeAreaMarginY * 0.9 );
        this._layers.bg.addChild( this._compass );
        this._compass.onComplete = () => {
            ServiceLocator.game.tweenGroup.add( 
                gsap.delayedCall( 10 + Math.random() * 5, () => { this._compass.gotoAndPlay( 0 ); } ) );
        };
        //this._compass.gotoAndPlay( 0 );

        this._playOrderArrow = new PIXI.Sprite( ServiceLocator.resources.play_order_arrow.texture );
        this._playOrderArrow.visible = false;
        this._playOrderArrow.blendMode = PIXI.BLEND_MODES.MULTIPLY;
        this._playOrderArrow.anchor.set( 0.5 );
        this._playOrderArrow.position.set( ServiceLocator.app.width * 0.5, ServiceLocator.app.height * 0.5 );
        this._layers.bg.addChild( this._playOrderArrow );

        this._playOrderArrowOverlay = new PIXI.Sprite();
        this._playOrderArrowOverlay.anchor.set( 0.5 );
        this._playOrderArrowOverlay.position.set( ServiceLocator.app.width * 0.5, ServiceLocator.app.height * 0.5 );
        this._layers.bg.addChild( this._playOrderArrowOverlay );
        //
        this._playOrderArrowOverlay.visible = false;

        this._playAreaCenter = new PIXI.Point();
        
        let arrTempCard: Array<any> = [];
        for ( let i: number = 0; i < 4; ++i )
        {
            for ( let j: number = 0; j < 10; ++j )
            {
                const kTempCard: any = { color: j == 8 ? card_color_type.ANY : i, value: j };
                arrTempCard.push( kTempCard, kTempCard ); 
            }
            for ( let j: number = 0; j < 2; ++j )
            {
                arrTempCard.push( { color: i, value: card_value_type.SKIP } );
                arrTempCard.push( { color: i, value: card_value_type.REVERSE } );
                arrTempCard.push( { color: i, value: card_value_type.DRAW_TWO } );
            }
        }
        for ( let i: number = 0; i < 4; ++i )
        {
            arrTempCard.push( { color: card_color_type.ANY, value: card_value_type.WILD_DRAW_FOUR } );
        }
        Utils.shufflePRNG( arrTempCard );

        this._deckWrapper = new PIXI.Container();
        this._deckWrapper.position.set( 256, 572 );
        this._layers.deck.addChild( this._deckWrapper );
        //
        /*this._deckShadow = new PIXI.Sprite( ServiceLocator.resources.deck_shadow.texture );
        this._deckShadow.anchor.set( 1 );
        this._deckShadow.position.set( this._deckShadow.texture.width * 0.5 + this._kDeckShadowOffsetX, this._deckShadow.texture.height * 0.5 );
        this._deckWrapper.addChild( this._deckShadow );*/
        //
        this._arrCard = new Array<Card>();
        this._arrDeckCard = new Array<Card>();
        let cardOffset: PIXI.Point = new PIXI.Point();
        let subDeckCardCount: number = 0;
        for ( let tempCard of arrTempCard )
        {
            let card = new Card();
            card.id = this._arrDeckCard.length;
            card.color = tempCard.color;
            card.value = tempCard.value;
            card.init();
            card.position.set( cardOffset.x, cardOffset.y );
            this._deckWrapper.addChild( card );
            
            this._arrCard.push( card );
            this._arrDeckCard.push( card );

            if ( subDeckCardCount >= 10)
            {
                subDeckCardCount = 0;

                cardOffset.y -= 6;
            }
            
            subDeckCardCount += 1;
        }

        // Lead card setup.
        for ( let i: number = this._arrDeckCard.length - 1; i >= 0; --i )
        {
            if ( this._arrDeckCard[ i ].value == card_value_type.NINE
                || this._arrDeckCard[ i ].value < card_value_type.EIGHT )
            {
                this._leadCard = this._arrDeckCard.splice( i, 1 )[ 0 ];
                break;
            }
        }
        this._leadCard.position.copyFrom( this._deckWrapper.position );
        this._layers.deck.addChildAt( this._leadCard, 0 );
        this._leadCard.setFaceUp();
        this._leadCard.visible = false;
        this._leadCardColor = this._leadCard.color;
        this._arrPlayedCard = new Array<Card>();
        this._arrPlayedCard.push( this._leadCard );
        //
        let leadCardShadow: PIXI.Sprite = new PIXI.Sprite( ServiceLocator.resources.deck_shadow.texture );
        leadCardShadow.visible = false;
        leadCardShadow.anchor.set( 0.5 );
        leadCardShadow.position.copyFrom( this._leadCard.position );
        leadCardShadow.x -= 10;
        leadCardShadow.y -= 10;
        this._layers.shadow.addChild( leadCardShadow );
        //
        this._leadCard.shadow = leadCardShadow;

        this._deckShadow = new PIXI.Container();
        this._deckShadow.position.copyFrom( this._deckWrapper.position );
        this._layers.shadow.addChild( this._deckShadow );

        // TEST:
        /*while ( this._arrDeckCard.length > 29 )
        {
            this._arrPlayedCard.push( this._arrDeckCard.pop() );
        }*/
        //

        /*this._sparkEmitter = new pp.Emitter(
            new PIXI.Container(),
            pp.upgradeConfig( 
                ServiceLocator.resources[ "emitter_sparks" ].data, 
                [ 
                    ServiceLocator.resources[ "spark_00" ].texture, 
                    ServiceLocator.resources[ "spark_01" ].texture, 
                    ServiceLocator.resources[ "spark_02" ].texture, 
                    ServiceLocator.resources[ "spark_03" ].texture, 
                    ServiceLocator.resources[ "spark_04" ].texture, 
                    ServiceLocator.resources[ "spark_05" ].texture
                ] ) );
        this._sparkEmitter.autoUpdate = false;
        this._sparkEmitter.emit = false;
        this._sparkEmitter.parent.position.set( 412, 572 );
        this._layers.vfx.addChild( this._sparkEmitter.parent );*/

        this._wildCardFrameAnimation = new PIXI.AnimatedSprite( Utils.createTexArray( "frame_anim_", 27 ) );
        this._wildCardFrameAnimation.visible = false;
        this._wildCardFrameAnimation.autoUpdate = false;
        this._wildCardFrameAnimation.loop = false;
        this._wildCardFrameAnimation.animationSpeed = 40;
        this._wildCardFrameAnimation.anchor.set( 0.5 );
        this._layers.deck.addChild( this._wildCardFrameAnimation );
        this._wildCardFrameAnimation.onComplete = () => {
            this._wildCardFrameAnimation.visible = false;
        };

        this._sparkAnimation = new PIXI.AnimatedSprite( Utils.createTexArray( "sparks_anim_", 29 ) );
        this._sparkAnimation.visible = false;
        this._sparkAnimation.autoUpdate = false;
        this._sparkAnimation.loop = false;
        this._sparkAnimation.animationSpeed = 40;
        this._sparkAnimation.anchor.set( 0.5 );
        this._layers.deck.addChild( this._sparkAnimation );
        this._sparkAnimation.onComplete = () => {
            this._sparkAnimation.visible = false;
        };

        this._drawCardTriggerArea = new PIXI.Graphics();
        this._drawCardTriggerArea.interactive = true;
        this._drawCardTriggerArea.buttonMode = true;
        /*Utils.createButton( "Draw" );
        this._drawCardTriggerArea.anchor.set( 0.5 );
        this._drawCardTriggerArea.visible = false;
        this._drawCardTriggerArea.position.copyFrom( this._arrDeckCard[ this._arrDeckCard.length - 1 ].position );*/ 
        this._layers.deck.addChild( this._drawCardTriggerArea );

        let myPlayerIndex: number = 0;
        for ( let profile of Session.i.room )
        {
            if ( Session.i.profile.uid == profile.uid )
            {
                break;
            }

            ++myPlayerIndex;
        }
            
        this._arrPlayer = new Array<Player>();
        //
        let playerIndex: number = myPlayerIndex;
        let playerSide: game_side_type = game_side_type.SOUTH;
        while ( this._arrPlayer.length < Session.i.room.length )
        {
            let player: Player = new Player();
            player.id = playerIndex;
            player.name = Session.i.room[ playerIndex ].username.prefix;
            player.portraitUrl = Session.i.room[ playerIndex ].avatar.src;
            player.side = playerSide;
            player.isBot = ( Session.i.room[ playerIndex ] as any ).isBot != undefined;

            let isInsert: boolean = false;
            for ( let i: number = 0; i < this._arrPlayer.length; ++i )
            {
                if ( this._arrPlayer[ i ].id > playerIndex )
                {
                    this._arrPlayer.splice( i, 0, player );
                    isInsert = true;
                    break;
                }
            }
            if ( !isInsert )
            {
                this._arrPlayer.push( player );
            }

            this._layers.player.addChild( player );

            if ( !this._myPlayer && playerIndex == myPlayerIndex )
            {
                this._myPlayer = player;
            }

            playerSide += 1;
            if ( playerSide == Session.i.room.length )
            {
                playerSide = 0;
            }
            if ( Session.i.gameType == game_type.PRIVATE_1_VS_4 
                && Session.i.room.length == Session.i.kGameRoomSize - 1 
                && playerSide == game_side_type.NORTH )
            {
                playerSide += 1;
            }

            playerIndex += 1;
            if ( playerIndex == Session.i.room.length )
            {
                playerIndex = 0;
            }
        }
        //
        for ( let player of this._arrPlayer )
        {
            player.init();
            player.onTurnAnimCompleted.add( this._onPlayer_TurnAnimCompleted, this );
        }

        for ( let i: number = 0; i < this._arrPlayer.length; ++i )
        {
            let nextPlayerIndex: number = i + 1;
            if ( nextPlayerIndex >= this._arrPlayer.length )
            {
                nextPlayerIndex = 0;
            }
            this._arrPlayer[ i ].nextPlayer = this._arrPlayer[ nextPlayerIndex ];

            let prevPlayerIndex: number = i - 1;
            if ( prevPlayerIndex < 0 )
            {
                prevPlayerIndex = this._arrPlayer.length - 1;
            }
            this._arrPlayer[ i ].prevPlayer = this._arrPlayer[ prevPlayerIndex ];
        }
        this._myPlayer.setScore( this._findPlayerScore( this._myPlayer ) );

        this._drawStackNotification = new DrawCountNotification();
        this._drawStackNotification.visible = false;
        this._drawStackNotification.position.copyFrom( this._myPlayer.handPos );
        this._drawStackNotification.y += 40;
        this._layers.vfx.addChild( this._drawStackNotification );

        this._autoplayNotification = new AutoplayNotification();
        this._autoplayNotification.visible = false;
        this._autoplayNotification.position.copyFrom( this._drawStackNotification.position );
        this._layers.vfx.addChild( this._autoplayNotification );

        this._comicBubble = new ComicBubble();
        this._comicBubble.visible = false;
        this._layers.vfx.addChild( this._comicBubble );

        this._hand = new PIXI.Sprite( ServiceLocator.resources.hand.texture );
        this._hand.anchor.set( 0.95, 0.86 );
        this._hand.visible = false;
        this._layers.vfx.addChild( this._hand );

        this._playAreaCenter.set( 320, 572 );

        this._shinyBandShader = new ShinyBandShader();
        this._shinyBandShader.enabled = false;

        if ( ServiceLocator.saved.data.isFirstGame )
        {
            /*let tutorialPopup: TutorialPopup = new TutorialPopup();
            tutorialPopup.init();
            tutorialPopup.onFirstGameStarted();
            this._root.addChild( tutorialPopup );
            tutorialPopup.onClosed.add( ( popup: TutorialPopup ) => {
                popup.end();

                this._input.setEnabled( true );*/

                this._playDeckEntryAnimation( true );
            /*} );

            this._input.setEnabled( false );*/
        }
        else 
        {
            ServiceLocator.multiplayerEventManager.onEventsReceived.add( this._onMultiplayer_EventsReceived, this );
            ServiceLocator.multiplayerEventManager.onPlayerPresenceUpdated.add( this._onMultiplayer_PlayerPresenceUpdated, this );

            this._playDeckEntryAnimation( true );
        }

        ServiceLocator.multiplayerEventManager.onProfileChange.add( this._onProfile_Changed, this );
    }

        private _playDeckEntryAnimation( isFirstRound: boolean ): void
        {
            this._deckEntryAnimation = new DeckEntryAnimation();
            this._deckEntryAnimation.isFirstRound = isFirstRound;
            this._deckEntryAnimation.deckWrapper = this._deckWrapper;
            this._deckEntryAnimation.deck = this._arrDeckCard;
            this._deckEntryAnimation.leadCard = this._leadCard;
            this._deckEntryAnimation.deckShadow = this._deckShadow;
            this._deckEntryAnimation.tentacle = this._tentacle;
            this._deckEntryAnimation.compass = this._compass;
            this._deckEntryAnimation.playOrderArrow = this._playOrderArrow;
            this._deckEntryAnimation.players = this._arrPlayer;
            this._deckEntryAnimation.gameLayers = this._layers;
            this._deckEntryAnimation.init();
            //
            this._deckEntryAnimation.onCompleted.addOnce( () => {
                this._deckEntryAnimation.end();
                this._deckEntryAnimation = null;

                this._startingDeckSize = this._arrDeckCard.length;

                this._myPlayer.setScore( this._findPlayerScore( this._myPlayer ) );

                if ( Session.i.gameType == game_type.PRIVATE_1_VS_4 )
                {
                    for ( let player of this._arrPlayer )
                    {
                        if ( player.isBot )
                        {
                            if ( !this._arrMissingPlayerId.includes( player.id ) )
                            {
                                this._arrMissingPlayerId.push( player.id );
                            }
                        }
                    }
                }

                // TEST:
                this._nextPlayer( Session.i.gameType == game_type.MATCHMAKING ? this._findNextPlayer( this._arrPlayer[ 0 ], this._matchMakingData.round - 1 ) : this._arrPlayer[ 0 ] ); 
                //this._nextPlayer( this._arrPlayer[ 3 ] ); 
            } );
            this._deckEntryAnimation.play();

            for ( let player of this._arrPlayer )
            {
                for ( let card of player.hand )
                {
                    let shadow: PIXI.Sprite = new PIXI.Sprite( ServiceLocator.resources.deck_shadow.texture );
                    shadow.anchor.set( 0.5 );
                    this._layers.shadow.addChild( shadow );
                    //
                    card.shadow = shadow;
                }
            }
        }

        private _onPlayer_TurnAnimCompleted():  void
        {
            this._managePlayerTurn( this._curPlayer ); 

            this._isNextAction = true;
        }

        private _onDrawCardButton_Click(): void
        {
            this._isNextAction = false;
            //
            this._playerInactivityTimer = 0;

            let arrDrawnCard: Array<Card> = this._drawCards( this._curPlayer, 1 );
            this._drawCardTl.call( ( drawnCard: Card ) => {
                    if ( this._isValidCard( drawnCard, this._curPlayer ) )
                    {
                        this._isNextAction = true;
                        
                        this._managePlayerTurn( this._curPlayer );
                    }
                    else
                    {
                        this._nextPlayer();
                    }
                }, [ arrDrawnCard[ 0 ] ] );

            this._drawCardTriggerArea.visible = false;
            if ( this._drawAlertTl )
            {
                this._drawAlertTl.kill();
                this._hand.visible = false;
                this._arrDeckCard[ this._arrDeckCard.length - 1 ].setHighlighted( false );
                this._arrDeckCard[ this._arrDeckCard.length - 1 ].scale.set( 1 );
                arrDrawnCard[ 0 ].filters = [];
                this._shinyBandShader.enabled = false;
            }

            if ( !ServiceLocator.saved.data.isFirstGame )
            {
                window.GameSnacks.liveMultiplayer.sendEvent( { payload: JSON.stringify( { type: game_event_type.CARD_DRAWN, playerId: this._curPlayer.id } ) } );
            }
        }

        private _nextPlayer( player: Player = null ): void
        {
            this._isNextAction = false;
            this._playerInactivityTimer = 0;
            this._imAliveTimer = 0;

            const kPrevPlayer: Player = this._curPlayer;
            if ( kPrevPlayer )
            {
                if ( kPrevPlayer.isAway )
                {
                    kPrevPlayer.isAway = false;
                    kPrevPlayer.isBot = undefined;
                }

                if ( kPrevPlayer.hand.length == 1 )
                {
                    gsap.killTweensOf( this._comicBubble );
                    //
                    const kScaleX: number = kPrevPlayer.side == game_side_type.WEST ? -1 : 1;
                    this._comicBubble.setText( "Last Card!", kScaleX );
                    this._comicBubble.setPlayerSide( kPrevPlayer.side );
                    this._comicBubble.visible = true;
                    this._comicBubble.scale.x = kScaleX;
                    ServiceLocator.game.tweenGroup.add(
                        gsap.from( this._comicBubble.scale, { x: 0, y: 0, duration: 1, ease: "elastic.out", onComplete: () => { this._comicBubble.visible = false; } } ) ); 
                }
            }

            if ( this._curPlayer && this._curPlayer != player )
            {
                if ( this._curPlayer.side == game_side_type.SOUTH )
                {
                    this._curPlayer.setHandInteractive( false );
                    this._drawCardTriggerArea.visible = false;
                    this._input.clearSelectedCard();
                }
                this._curPlayer.setHighlighted( false );
            
                for ( let card of this._curPlayer.hand )
                {
                    card.shade.visible = false;
                }
            }

            if ( !player )
            {
                this._curPlayer = this._findNextPlayer( this._curPlayer, 1 );
            }
            else
            {
                this._curPlayer = player;
            }

            //console.log( this._curPlayer.id + ":: next player" );

            this._onPlayer_TurnAnimCompleted();
        }

            private _managePlayerTurn( player: Player ): void
            {
                let isValidCard: boolean = false;
                for ( let card of player.hand )
                {
                    isValidCard = this._isValidCard( card, player );
                    if ( isValidCard )
                    {
                        break;
                    }
                }
                if ( !isValidCard )
                {
                    if ( player.isBot )
                    {
                        ServiceLocator.game.tweenGroup.add( 
                            gsap.delayedCall( player.isAway ? 0 : 0.2 + Math.random() * 0.4, () => {
                                if ( player.isAway && player == this._myPlayer )
                                {
                                    this._drawCardTriggerArea.visible = false;
                                    if ( this._drawAlertTl )
                                    {
                                        this._drawAlertTl.kill();
                                        this._hand.visible = false;
                                        this._arrDeckCard[ this._arrDeckCard.length - 1 ].setHighlighted( false );
                                        this._arrDeckCard[ this._arrDeckCard.length - 1 ].scale.set( 1 );
                                        this._arrDeckCard[ this._arrDeckCard.length - 1 ].filters = [];
                                        this._shinyBandShader.enabled = false;
                                    }

                                    this._showAutoplayNotification();
                                }

                                this._isNextAction = false;

                                let arrDrawnCard: Array<Card> = this._drawCards( player, 1 );
                                this._drawCardTl.call( ( drawnCard: Card ) => {
                                        if ( this._isValidCard( drawnCard, player ) )
                                        {
                                            this._isNextAction = true;

                                            ServiceLocator.game.tweenGroup.add( 
                                                gsap.delayedCall( player.isAway ? 0 : 0.6 + Math.random() * 1.4, () => { 
                                                    this._isNextAction = false;

                                                    this._makePlay( player ); 
                                                } ) 
                                            );
                                        }
                                        else
                                        {
                                            this._nextPlayer();
                                        }
                                    }, [ arrDrawnCard[ 0 ] ] );
                            } ) );
                    }
                    else if ( player == this._myPlayer )
                    {
                        let topCardPos: PIXI.Point = this._arrDeckCard[ this._arrDeckCard.length - 1 ].position.clone();
                        topCardPos.x += this._deckWrapper.x;
                        topCardPos.y += this._deckWrapper.y;

                        this._drawCardTriggerArea.visible = true;
                        this._drawCardTriggerArea.beginFill( 0xffffff, 0.0001 );
                        this._drawCardTriggerArea.position.copyFrom( topCardPos );
                        this._drawCardTriggerArea.drawRect( -this._arrDeckCard[ this._arrDeckCard.length - 1 ].width * 0.5, -this._arrDeckCard[ this._arrDeckCard.length - 1 ].height * 0.5, this._arrDeckCard[ this._arrDeckCard.length - 1 ].width, this._arrDeckCard[ this._arrDeckCard.length - 1 ].height );
                        this._drawCardTriggerArea.endFill();
                        //
                        this._drawCardTriggerArea.off( "pointertap" );
                        this._drawCardTriggerArea.on( "pointertap", this._onDrawCardButton_Click, this );

                        if ( this._drawAlertTl )
                        {
                            this._drawAlertTl.kill();
                        }
                        this._hand.visible = true;
                        this._hand.position.copyFrom( topCardPos );
                        this._hand.y -= 30;
                        this._drawAlertTl = new gsap.core.Timeline();
                        this._drawAlertTl.repeat( -1 );
                        this._drawAlertTl.repeatDelay( 0.4 );
                        this._drawAlertTl.call( () => { this._shinyBandShader.reset(); }, null, 0 );
                        this._drawAlertTl.from( this._hand, { x: "-=30", y: "-=50", duration: 1, ease: "sine.out" }, 0.4 );
                        this._drawAlertTl.to( this._hand, { y: "+=20", duration: 0.1, repeat: 1, yoyo: true, ease: "sine.in" }, 1.4 );
                        this._drawAlertTl.to( this._arrDeckCard[ this._arrDeckCard.length - 1 ].scale, { x: 1.1, y: 1.1, duration: 0.04, ease: "sine.out" }, 1.5 );
                        this._drawAlertTl.to( this._arrDeckCard[ this._arrDeckCard.length - 1 ].scale, { x: 1, y: 1, duration: 0.04, ease: "sine.in" }, ">" );
                        this._drawAlertTl.to( this._hand, { y: "+=20", duration: 0.1, repeat: 1, yoyo: true, ease: "sine.in" }, 1.7 );
                        this._drawAlertTl.to( this._arrDeckCard[ this._arrDeckCard.length - 1 ].scale, { x: 1.1, y: 1.1, duration: 0.04, ease: "sine.out" }, 1.8 );
                        this._drawAlertTl.to( this._arrDeckCard[ this._arrDeckCard.length - 1 ].scale, { x: 1, y: 1, duration: 0.04, ease: "sine.in" }, ">" );
                        this._drawAlertTl.to( this._hand, { x: topCardPos.x - 30, y: topCardPos.y - 30 - 50, duration: 1, ease: "sine.out" }, 2 );
                        ServiceLocator.game.tweenGroup.add( this._drawAlertTl );
                        //
                        this._arrDeckCard[ this._arrDeckCard.length - 1 ].setHighlighted( true );
                        this._arrDeckCard[ this._arrDeckCard.length - 1 ].filters = [ this._shinyBandShader ];

                        player.setHandInteractive( true );

                        this._input.onCardSelected.add( this._onPlayerCard_Selected, this );

                        for ( let card of player.hand )
                        {
                            card.shade.visible = true;
                        }
                    }
                }
                else
                {
                    if ( player.isBot )
                    {
                        if ( player.isAway && player == this._myPlayer )
                        {
                            this._showAutoplayNotification();
                        }

                        ServiceLocator.game.tweenGroup.add(
                            gsap.delayedCall( player.isAway ? 0 : 0.6 + Math.random() * 1.4, () => { 
                                this._isNextAction = false;
                                
                                this._makePlay( player ); 
                            } ) 
                        );
                    }
                    else 
                    {
                        if ( player.side == game_side_type.SOUTH )
                        {
                            this._drawCardTriggerArea.visible = true;
                            this._drawCardTriggerArea.beginFill( 0xffffff, 0.0001 );
                            let topCardPos: PIXI.Point = this._arrDeckCard[ this._arrDeckCard.length - 1 ].position.clone();
                            topCardPos.x += this._deckWrapper.x;
                            topCardPos.y += this._deckWrapper.y;
                            this._drawCardTriggerArea.position.copyFrom( topCardPos );
                            this._drawCardTriggerArea.position.copyFrom( this._arrDeckCard[ this._arrDeckCard.length - 1 ].position );
                            this._drawCardTriggerArea.drawRect( -this._arrDeckCard[ this._arrDeckCard.length - 1 ].width * 0.5, -this._arrDeckCard[ this._arrDeckCard.length - 1 ].height * 0.5, this._arrDeckCard[ this._arrDeckCard.length - 1 ].width, this._arrDeckCard[ this._arrDeckCard.length - 1 ].height );
                            this._drawCardTriggerArea.endFill();
                            //
                            this._drawCardTriggerArea.off( "pointertap" );
                            this._drawCardTriggerArea.on( "pointertap", this._wiggleCard.bind( this, this._arrDeckCard[ this._arrDeckCard.length - 1 ] ), this );

                            player.setHandInteractive( true );

                            for ( let card of player.hand )
                            {
                                card.shade.visible = !this._isValidCard( card, player );
                            }

                            this._input.onCardSelected.add( this._onPlayerCard_Selected, this );
                        }
                    }
                }
                
                player.setHighlighted( true );
            }

                private _showAutoplayNotification(): void
                {
                    this._autoplayNotification.visible = true;
                    this._autoplayNotification.alpha = 1;
                    ServiceLocator.game.tweenGroup.add(
                        gsap.to( this._autoplayNotification, { alpha: 0, duration: 0.6, ease: "sine.in", delay: 1, onComplete: () => { this._autoplayNotification.visible = false; } } ) );
                }

                private _wiggleCard( card: Card ): void
                {
                    if ( this._cardWiggleTl )
                    {
                        this._cardWiggleTl.progress( 1 );
                        this._cardWiggleTl.kill();
                    }
                    const kCardX: number = card.x;
                    this._cardWiggleTl = gsap.timeline()
                        .to( card, { x: kCardX - 10, duration: 0.1 } )
                        .to( card, { x: kCardX + 10, duration: 0.1 } )
                        .to( card, { x: kCardX - 8, duration: 0.1 } )
                        .to( card, { x: kCardX + 8, duration: 0.1 } )
                        .to( card , { x: kCardX, duration: 0.1 } ) ;
                    ServiceLocator.game.tweenGroup.add( this._cardWiggleTl );
                }

            private _findNextPlayer( player: Player, steps: number ): Player
            {
                let result: Player = null;
                
                if ( steps <= 0 )
                {
                    result = player;
                }
                else
                {
                    result = this._playOrder == 1 ? player.nextPlayer : player.prevPlayer;
            
                    steps -= 1;
                    while ( steps > 0 )
                    {
                        result = this._playOrder == 1 ? result.nextPlayer : result.prevPlayer;

                        steps -= 1;
                    }
                }
        
                return result;
            }

            private _drawCards( player: Player, count: number ): Array<Card>
            {
                if ( this._drawCardTl )
                {
                    this._drawCardTl.kill();
                }
                this._drawCardTl = new gsap.core.Timeline();

                let arrDrawnCard: Array<Card> = [];
                for ( let i: number = 0; i < count; ++i )
                {
                    let drawnCard: Card = this._arrDeckCard.pop();
                    drawnCard.setHighlighted( false );
                    drawnCard.scale.set( 1 );
                    drawnCard.x += this._deckWrapper.x;
                    drawnCard.y += this._deckWrapper.y;
                    arrDrawnCard.push( drawnCard );
                    this._layers.card.addChild( drawnCard );

                    this._deckShadow.removeChildAt( this._deckShadow.children.length - 1 );
                    //this._deckShadow.x = this._deckShadow.texture.width * 0.5 + ( this._kDeckShadowOffsetX * this._arrDeckCard.length / this._startingDeckSize );
                
                    if ( this._arrDeckCard.length == 0 )
                    {
                        break;
                    }
                }

                const kArrHandCardLayout: Array<ICardLayout> = player.findPostDrawAnimGlobalHandLayout( arrDrawnCard );
                let time: number = 0;
                for ( let drawnCard of arrDrawnCard )
                {
                    this._addDrawnCardToTimeline( drawnCard, player, kArrHandCardLayout, time );

                    time += 0.3;
                }
                //
                for ( let cardLayout of kArrHandCardLayout )
                {
                    if ( cardLayout.card.parent != this._layers.card )
                    {
                        this._drawCardTl.to( cardLayout.card.parent, { rotation: cardLayout.rotation - [ 0, Math.PI * 0.5, 0, -Math.PI * 0.5 ][ player.side ], duration: 0.1 }, 0 );
                    }
                }
                this._drawCardTl.call( () => {
                    for ( let card of player.hand )
                    {
                        card.visible = false;
                    }

                    for ( let cardLayout of kArrHandCardLayout )
                    {
                        if ( arrDrawnCard.includes( cardLayout.card ) )
                        {
                            this._layers.card.addChild( cardLayout.card );
                        }
                        else
                        {
                            let dummyCard: PIXI.Sprite = new PIXI.Sprite();
                            dummyCard.texture = cardLayout.card.faceTexture;
                            dummyCard.scale.set( 0.5 );
                            if ( player.side == game_side_type.NORTH )
                            {
                                dummyCard.scale.y *= -1;
                            }
                            dummyCard.anchor.set( 0.5 );
                            dummyCard.position.copyFrom( this._layers.card.toLocal( new PIXI.Point( cardLayout.x, cardLayout.y ) ) );
                            dummyCard.rotation = cardLayout.rotation;
                            this._layers.card.addChild( dummyCard );

                            this._arrDummyHandCard.push( dummyCard );
                        }
                    }
                }, null, ">" );

                if ( this._arrDeckCard.length == 0 )
                {
                    // Shuffle a new deck.
                    while ( this._arrPlayedCard.length > 1 )
                    {
                        let playedCard: Card = this._arrPlayedCard.shift();
                        playedCard.angle = 0;
                        if ( ( playedCard.value == card_value_type.EIGHT || playedCard.value == card_value_type.WILD_DRAW_FOUR ) 
                            && playedCard.color != card_color_type.ANY )
                        {
                            playedCard.setColor( card_color_type.ANY );
                        }
                        this._arrDeckCard.push( playedCard );
                    }
                    Utils.shufflePRNG( this._arrDeckCard );
                    let subDeckCardCount: number = 0;
                    let cardOffset: PIXI.Point = new PIXI.Point();
                    for ( let card of this._arrDeckCard )
                    {
                        card.setFaceDown();
                        card.position.set( cardOffset.x, cardOffset.y );
                        this._deckWrapper.addChild( card );

                        if ( subDeckCardCount >= 10)
                        {
                            subDeckCardCount = 0;

                            cardOffset.y -= 6;
                        }

                        subDeckCardCount += 1;
                    }

                    //this._deckShadow.visible = false;
                    //this._deckShadow.x = this._deckShadow.texture.width * 0.5 + this._kDeckShadowOffsetX;
                    this._startingDeckSize = this._arrDeckCard.length;
                    
                    const kRemainingCount: number = count - arrDrawnCard.length;
                    //
                    let arrPostDrawnCard: Array<Card> = [];
                    for ( let j: number = 0; j < kRemainingCount; ++j )
                    {
                        arrPostDrawnCard.push( this._arrDeckCard.pop() );
                    }

                    for ( let card of this._arrDeckCard )
                    {
                        let cardShadow: PIXI.Sprite = new PIXI.Sprite( ServiceLocator.resources.deck_shadow.texture );
                        cardShadow.anchor.set( 0.5 );
                        cardShadow.position.copyFrom( card.position );
                        cardShadow.x -= 10;
                        cardShadow.y -= 10;
                        this._deckShadow.addChild( cardShadow );
                    }

                    this._deckCreationAnimation = new DeckCreationAnimation();
                    this._deckCreationAnimation.deck = this._arrDeckCard;
                    this._deckCreationAnimation.deckShadow = this._deckShadow;
                    this._deckCreationAnimation.init();
                    //
                    this._deckCreationAnimation.onCompleted.addOnce( () => {
                        this._deckCreationAnimation.end();
                        this._deckCreationAnimation = null;
                    } );
                    const kDrawAnimDuration: number = this._drawCardTl.totalDuration();
                    this._drawCardTl.call( () => { 
                        this._deckShadow.visible = true;
                        this._deckCreationAnimation.play(); 
                    }, null, kDrawAnimDuration );

                    this._drawCardTl.call( () => {
                        for ( let dummyCard of this._arrDummyHandCard )
                        {
                            dummyCard.parent.removeChild( dummyCard );
                        }
                        this._arrDummyHandCard.splice( 0 );
                        //
                        for ( let card of this._curPlayer.hand )
                        {
                            card.visible = true;
                        }
                    } );

                    const kArrHandCardLayout: Array<ICardLayout> = player.findPostDrawAnimGlobalHandLayout( arrPostDrawnCard );
                    const kDrawAnimTime: number = kDrawAnimDuration + this._deckCreationAnimation.calcDuration() + 0.1;
                    time = kDrawAnimTime;
                    for ( let drawnCard of arrPostDrawnCard )
                    {
                        this._addDrawnCardToTimeline( drawnCard, player, kArrHandCardLayout, time );
                        
                        time += 0.3;
                    }
                    //
                    for ( let cardLayout of kArrHandCardLayout )
                    {
                        if ( cardLayout.card.parent != this._layers.card )
                        {
                            this._drawCardTl.to( cardLayout.card.parent, { rotation: cardLayout.rotation - [ 0, Math.PI * 0.5, 0, -Math.PI * 0.5 ][ player.side ], duration: 0.1 }, kDrawAnimTime );
                        }
                    }
                    this._drawCardTl.call( () => {
                        for ( let card of player.hand )
                        {
                            card.visible = false;
                        }

                        for ( let cardLayout of kArrHandCardLayout )
                        {
                            if ( arrDrawnCard.includes( cardLayout.card ) )
                            {
                                this._layers.card.addChild( cardLayout.card );
                            }
                            else
                            {
                                let dummyCard: PIXI.Sprite = new PIXI.Sprite();
                                dummyCard.texture = cardLayout.card.faceTexture;
                                dummyCard.scale.set( 0.5 );
                                if ( player.side == game_side_type.NORTH )
                                {
                                    dummyCard.scale.y *= -1;
                                }
                                dummyCard.anchor.set( 0.5 );
                                dummyCard.position.copyFrom( this._layers.card.toLocal( new PIXI.Point( cardLayout.x, cardLayout.y ) ) );
                                dummyCard.rotation = cardLayout.rotation;
                                this._layers.card.addChild( dummyCard );

                                this._arrDummyHandCard.push( dummyCard );
                            }
                        }
                    }, null, ">" );
                }

                this._drawCardTl.call( () => {
                    for ( let dummyCard of this._arrDummyHandCard )
                    {
                        dummyCard.parent.removeChild( dummyCard );
                    }
                    this._arrDummyHandCard.splice( 0 );
                    //
                    for ( let card of player.hand )
                    {
                        card.visible = true;
                    }

                    if ( player == this._myPlayer )
                    {
                        player.sortHandFaceUp();

                        player.setScore( this._findPlayerScore( player ) );
                    }
                    else
                    {
                        player.sortHandFaceDown();
                    }
                } );
                    
                ServiceLocator.game.tweenGroup.add( this._drawCardTl );

                return arrDrawnCard;
            }

                private _addDrawnCardToTimeline( drawnCard: Card, player: Player, arrHandCardLayout: Array<ICardLayout>, time: number = 0 ): void
                {
                    let drawnCardLayout: ICardLayout = null;
                    for ( let cardLayout of arrHandCardLayout )
                    {
                        if ( cardLayout.card == drawnCard )
                        {
                            drawnCardLayout = cardLayout;
                            break;
                        }
                    }

                    this._drawCardTl.call( () => { 
                        if ( player.side == game_side_type.SOUTH )
                        {
                            drawnCard.setFaceUp();
                        }
                        drawnCard.setHighlighted( false );
                        drawnCard.scale.set( 1 );
                        //this._layers.card.addChild( drawnCard ); 
                        
                        let shadow: PIXI.Sprite = new PIXI.Sprite( ServiceLocator.resources.deck_shadow.texture );
                        shadow.anchor.set( 0.5 );
                        this._layers.card.addChildAt( shadow, 0 );
                        //
                        drawnCard.shadow = shadow;
                    }, null, time );
                    this._drawCardTl.from( drawnCard, { alpha: 0, duration: 0.175 }, time );
                    //this._drawCardTl.from( drawnCard, { angle: drawnCard.angle - 45, duration: 0.245, ease: "sine.out" }, 0 );
                    this._drawCardTl.from( drawnCard.scale, { x: 1.2, y: 1.2, duration: 0.245, ease: "sine.out" }, time );
                    //this._drawCardTl.to( drawnCard, { x: player.handPos.x + drawnCardTargetMarginX * Math.random() * drawnCardTargetSideX, y: player.handPos.y, duration: 0.35, ease: "sine.out" }, time + 0.175 );
                    const kTargetPos: PIXI.Point = this._layers.card.toLocal( new PIXI.Point( drawnCardLayout.x, drawnCardLayout.y ) );
                    this._drawCardTl.to( drawnCard, { x: kTargetPos.x, y: kTargetPos.y, rotation: drawnCardLayout.rotation, duration: 0.35, ease: "sine.out" }, time + 0.175 );
                    this._drawCardTl.call( () => { ServiceLocator.audio.playSfx( "slide_" + Utils.randomInteger( 0, 3 ).toString() ); }, null, time + 0.175 )
                    this._drawCardTl.call( ( card: Card ) => {
                        //card.rotation = 0;
                        player.addCard( card );
                        this._layers.shadow.addChild( card.shadow );
                    }, [ drawnCard ], time + 0.6 );
                }

            private _isValidCard( card: Card, player: Player ) : boolean
            {
                //return false; // TEST:
                let isValidCard: boolean = false;

                if ( this._drawStack > 1 )
                {
                    isValidCard = card.value == card_value_type.DRAW_TWO;
                }
                else
                {
                    /*if ( card.value == card_value_type.WILD_DRAW_FOUR )
                    {
                        let isColor: boolean = false;
                        for ( let cardAux of player.hand )
                        {
                            if ( cardAux.color == this._leadCardColor
                                || cardAux.value == card_value_type.EIGHT )
                            {
                                isColor = true;
                                break;
                            }
                        }
                        isValidCard = !isColor;
                    }
                    else
                    {*/
                        isValidCard = card.color == this._leadCardColor
                            || card.value == this._leadCard.value
                            || card.value == card_value_type.WILD_DRAW_FOUR
                            || card.value == card_value_type.EIGHT;
                    //}
                }

                return isValidCard;
            }

            private _onPlayerCard_Selected( card: Card ): void
            {
                let isValidCard: boolean = card.owner == this._curPlayer
                    && this._isValidCard( card, this._curPlayer );
                if ( isValidCard )
                {
                    this._isNextAction = false;
                    //
                    this._playerInactivityTimer = 0;

                    if ( this._cardWiggleTl )
                    {
                        this._cardWiggleTl.progress( 1 );
                        this._cardWiggleTl.kill();
                        this._cardWiggleTl = null;
                    }

                    card.owner.setHandInteractive( false );
                    this._drawCardTriggerArea.visible = false;
                    this._input.clearSelectedCard();

                    this._input.onCardSelected.remove( this._onPlayerCard_Selected, this );

                    if ( card.color == card_color_type.ANY
                        && this._myPlayer.hand.length - 1 > this._winCardCount )
                    {
                        this._colorSelectionPopup = new ColorSelectionPopup();
                        this._colorSelectionPopup.player = this._myPlayer;
                        this._colorSelectionPopup.selCard = card;
                        this._colorSelectionPopup.onEnter();
                        this._colorSelectionPopup.onColorSelectionStarted.addOnce( this._onColorSelectionPopup_SelectionStarted, this );
                        this._colorSelectionPopup.onColorSelected.addOnce( this._onColorSelectionPopup_ColorSelected, this );
                        this._colorSelectionPopup.onClosed.addOnce( this._onColorSelectionPopup_Closed, this );
                    }
                    else
                    {
                        this._playCard( card, this._curPlayer );
                        if ( !ServiceLocator.saved.data.isFirstGame )
                        {
                            window.GameSnacks.liveMultiplayer.sendEvent( { payload: JSON.stringify( { type: game_event_type.CARD_PLAYED, playerId: card.owner.id, cardId: card.id } ) } );
                        }

                        if ( card.owner == this._myPlayer )
                        {
                            this._myPlayer.setScore( this._findPlayerScore( this._myPlayer ) );
                        }
                    }
                }
                else /*if ( ServiceLocator.app.isTouchDevice )*/
                {
                    //this._input.clearSelectedCard();
                    this._wiggleCard( card );
                }
            }

                private _onColorSelectionPopup_SelectionStarted(): void
                {
                    this._isNextAction = true;
                    //
                    this._playerInactivityTimer = 0;
                }

                private _onColorSelectionPopup_ColorSelected(): void
                {
                    this._isNextAction = false;
                    //
                    this._playerInactivityTimer = 0;
                }

                private _onColorSelectionPopup_Closed( popup: ColorSelectionPopup ): void
                {
                    this._leadCardColor = popup.selColor;

                    this._playCard( popup.selCard, this._curPlayer );
                    //this._curPlayer.removeCard( card );
                    if ( !ServiceLocator.saved.data.isFirstGame )
                    {
                        window.GameSnacks.liveMultiplayer.sendEvent( { payload: JSON.stringify( { type: game_event_type.CARD_PLAYED, playerId: popup.selCard.owner.id, cardId: popup.selCard.id, selLeadColor: popup.selColor } ) } );
                    }

                    if ( popup.selCard.owner == this._myPlayer )
                    {
                        this._myPlayer.setScore( this._findPlayerScore( this._myPlayer ) );
                    }

                    popup.onLeave();
                    this._colorSelectionPopup = null;
                }


                private _findPlayerScore( player: Player ): number
                {
                    let result: number = 0;

                    for ( let card of player.hand )
                    {
                        let cardScore: number = 0;
                        if ( card.value < card_value_type.SKIP )
                        {
                            if ( card.value == card_value_type.EIGHT )
                            {
                                cardScore = 50;
                            }
                            else if ( card.value == card_value_type.NINE
                                || card.value == card_value_type.ZERO )
                            {
                                cardScore = 10;
                            }
                            else
                            {
                                cardScore = card.value;
                            }
                        }
                        else
                        {
                            if ( card.value == card_value_type.WILD_DRAW_FOUR )
                            {
                                cardScore = 50;
                            }
                            else
                            {
                                cardScore = 20;
                            }
                        }
                        result += cardScore;
                    }

                    return result;
                }

                private _makePlay( player: Player ): void
                {
                    //return; // TEST:
                    
                    let arrValidCard: Array<Card> = [];
                    for ( let card of player.hand )
                    {
                        if ( this._isValidCard( card, player ) )
                        {
                            if ( arrValidCard.length == 0 )
                            {
                                arrValidCard.push( card );
                            }
                            else
                            {
                                let cardInsertIndex: number = 0;
                                while ( cardInsertIndex < arrValidCard.length )
                                {
                                    if ( card.id < arrValidCard[ cardInsertIndex ].id )
                                    {
                                        arrValidCard.splice( cardInsertIndex, 0, card );
                                        
                                        break;
                                    }

                                    cardInsertIndex += 1;
                                }
                                if ( cardInsertIndex == arrValidCard.length )
                                {
                                    arrValidCard.push( card );
                                }
                            }
                        }
                    }

                    let arrCardId: Array<string> = [];
                    for ( let card of arrValidCard )
                    {
                        arrCardId.push( card.id.toString() );
                    }
                    const kSelCardIndex: number = Session.i.prng.randInterval( 0, arrValidCard.length - 1 );
                    const kSelCard: Card = arrValidCard[ kSelCardIndex ];
                    //console.log( "GamePlayScreen.ts :: makePlay() :: kSelCardIndex=" + kSelCardIndex.toString() + " :: kSelCard.id=" + kSelCard.id.toString() + " :: arrCardId=" + arrCardId.toString() );

                    this._playCard( kSelCard, player ); 
                }

                    private _playCard( card: Card, player: Player ): void
                    {
                        //console.log( this._curPlayer.id + ":: playCard=" + card.value );

                        this._layers.card.addChild( card.shadow );

                        const kGlobalPos: PIXI.Point = this._layers.card.toLocal( card.getGlobalPosition() );
                        player.removeCard( card );
                        this._layers.card.addChild( card );
                        card.position.copyFrom( kGlobalPos );
                        card.setFaceUp();

                        if ( player == this._myPlayer )
                        {
                            for ( let card of player.hand )
                            {
                                card.shade.visible = true;
                            }
                        }

                        ServiceLocator.audio.playSfx( "deal_flip_" + Utils.randomInteger( 0, 3 ).toString() );

                        if ( this._playCardTl )
                        {
                            this._playCardTl.kill();
                        }
                        this._playCardTl = new gsap.core.Timeline();
                        let isWild: boolean = card.value == card_value_type.EIGHT || card.value == card_value_type.WILD_DRAW_FOUR;
                        if ( isWild && player.hand.length > this._winCardCount )
                        {
                            let kDuration: number = [ 0.2, 0.24 , 0.3, 0.2 ][ player.side ];
                            this._playCardTl.to( card, { angle: ( Math.random() > 0.5 ? -4 : 4 ) * Math.random(), duration: kDuration, ease: "sine.out" }, 0 );
                            this._playCardTl.to( card, { x: this._kLeadCardPos.x + ( Math.random() > 0.5 ? -4 : 4 ) * Math.random(), y: this._kLeadCardPos.y + ( Math.random() > 0.5 ? -4 : 4 ) * Math.random(), duration: kDuration, ease: "linear.inOut" }, 0 );
                            this._playCardTl.call( ( card: Card ) => { 
                                this._layers.shadow.addChildAt( card.shadow, 0 );
                                card.shadow.position.copyFrom( card.position );
                                card.shadow.angle = card.angle;
                                card.shadow.scale.set( 1 );
                                //
                                let m: PIXI.Matrix = new PIXI.Matrix();
                                m.identity();
                                m.tx = -10;
                                m.ty = -10;
                                m.rotate( card.shadow.angle * Math.PI / 180 );
                                card.shadow.x += m.tx;
                                card.shadow.y += m.ty;

                                card.shadow = null;
                             }, [ card ], ">" );
                            this._playCardTl.to( card.scale, { x: 0.8, y: 0.8, duration: kDuration, ease: "sine.out" }, 0 );
                            this._playCardTl.call( ( card: Card ) => { this._onCardPlayed( card, player ); }, [ card ], ">" );
                        }
                        else
                        {
                            this._playCardTl.to( card, { angle: ( Math.random() > 0.5 ? -4 : 4 ) * Math.random(), duration: 0.4, ease: "sine.out" }, 0 );
                            this._playCardTl.to( card, { x: this._kLeadCardPos.x + ( Math.random() > 0.5 ? -4 : 4 ) * Math.random(), y: this._kLeadCardPos.y + ( Math.random() > 0.5 ? -4 : 4 ) * Math.random(), duration: 0.4, ease: "sine.out" }, 0 );
                            this._playCardTl.call( ( card: Card ) => { 
                                this._layers.shadow.addChildAt( card.shadow, 0 );
                                card.shadow.position.copyFrom( card.position );
                                card.shadow.angle = card.angle;
                                card.shadow.scale.set( 1 );
                                //
                                let m: PIXI.Matrix = new PIXI.Matrix();
                                m.identity();
                                m.tx = -10;
                                m.ty = -10;
                                m.rotate( card.shadow.angle * Math.PI / 180 );
                                card.shadow.x += m.tx;
                                card.shadow.y += m.ty;

                                card.shadow = null;
                             }, [ card ], ">" );
                            this._playCardTl.call( ( card: Card ) => { this._onCardPlayed( card, player ); }, [ card ], 0.6 );
                        }
                        
                        ServiceLocator.game.tweenGroup.add( this._playCardTl )
                    }

                        private _onCardPlayed( card: Card, player: Player ): void
                        {
                            this._arrPlayedCard.push( card );

                            this._layers.deck.addChild( card );
                        
                            if ( player == this._myPlayer )
                            {
                                player.sortHandFaceUp();
                                if ( player.isAway )
                                {
                                    player.setScore( this._findPlayerScore( player ) );
                                }
                            }
                            else
                            {
                                player.sortHandFaceDown();
                            }
                            
                            if ( player.hand.length == this._winCardCount )
                            {
                                this._state = state_type.GAME_OVER;

                                this._gameOverAnimation = new GameOverAnimation();
                                this._gameOverAnimation.isWin = this._curPlayer == this._myPlayer;
                                this._gameOverAnimation.winner = this._curPlayer;
                                this._gameOverAnimation.players = this._arrPlayer;
                                this._gameOverAnimation.gameLayers = this._layers;
                                this._gameOverAnimation.init();
                                this._root.addChild( this._gameOverAnimation );
                                this._gameOverAnimation.onCompleted.addOnce( this._onGameOverAnimation_Completed, this );
                                this._gameOverAnimation.play();
                            }
                            else
                            {
                                this._leadCard = card;
                                if ( card.color != card_color_type.ANY )
                                {
                                    this._leadCardColor = this._leadCard.color;
                                }

                                if ( card.value == card_value_type.EIGHT )
                                {
                                    if ( player.isBot )
                                    {
                                        let arrCardColor: Array<card_color_type> = [];
                                        for ( let card of player.hand )
                                        {
                                            if ( !arrCardColor.includes( card.color ) && card.color <= 3 )
                                            {
                                                arrCardColor.push( card.color );
                                            }
                                        }
                                        this._leadCardColor = arrCardColor[ Session.i.prng.randInterval( 0, arrCardColor.length - 1 ) ];
                                    }
                                        
                                    let dummyLeadCard: PIXI.Sprite = new PIXI.Sprite( this._leadCard.faceTexture );
                                    dummyLeadCard.anchor.set( 0.5 );
                                    dummyLeadCard.scale.set( 0.5 );
                                    this._leadCard.addChild( dummyLeadCard );

                                    this._leadCard.setColor( this._leadCardColor );
                                    //
                                    let tl: gsap.core.Timeline = new gsap.core.Timeline();
                                    tl.timeScale( 1.4 );
                                    tl.to( this._leadCard.scale, { x: 1.4, y: 1.4, duration: 0.2, ease: "sine.out" }, 0 );
                                    tl.to( this._leadCard.scale, { x: 1, y: 1, duration: 0.1, ease: "sine.in" }, 0.2 );
                                    tl.call( () => { 
                                        this._wildCardFrameAnimation.position.copyFrom( this._leadCard.position );
                                        this._layers.deck.addChildAt( this._wildCardFrameAnimation, this._layers.deck.children.length - 2 );
                                        this._wildCardFrameAnimation.visible = true;
                                        this._wildCardFrameAnimation.gotoAndPlay( 0 );

                                        this._sparkAnimation.position.copyFrom( this._leadCard.position );
                                        this._layers.deck.addChildAt( this._sparkAnimation, this._layers.deck.children.length - 2 );
                                        this._sparkAnimation.visible = true;
                                        this._sparkAnimation.gotoAndPlay( 0 );
                                        
                                        //this._sparkEmitter.playOnce(); 
                                    }, null, 0 );
                                    tl.to( this._leadCard, { angle: this._leadCard.angle + 15, duration: 0.075, ease: "sine.out" }, 0.1 );
                                    tl.to( this._leadCard, { angle: this._leadCard.angle - 15, duration: 0.15, ease: "sine.out" }, ">" );
                                    tl.to( this._leadCard, { angle: this._leadCard.angle, duration: 0.075 * 10, ease: "elastic.out" }, ">" );
                                    tl.to( dummyLeadCard, { alpha: 0, duration: 0.3, ease: "sine.in" }, 0.1 );
                                    tl.call( () => {
                                        ServiceLocator.audio.playSfx( "wild_card" );
                                    }, null, 0.1 );
                                    tl.call( () => { 
                                        dummyLeadCard.parent.removeChild( dummyLeadCard );
                                        dummyLeadCard.destroy();
                                        
                                        this._nextPlayer(); }, null, 0.4 );
                                    //
                                    ServiceLocator.game.tweenGroup.add( tl );
                                }
                                else if ( card.value == card_value_type.WILD_DRAW_FOUR )
                                {
                                    if ( player.isBot )
                                    {
                                        let arrCardColor: Array<card_color_type> = [];
                                        for ( let card of player.hand )
                                        {
                                            if ( !arrCardColor.includes( card.color ) && card.color <= 3 )
                                            {
                                                arrCardColor.push( card.color );
                                            }
                                        }
                                        this._leadCardColor = arrCardColor[ Session.i.prng.randInterval( 0, arrCardColor.length - 1 ) ];
                                    }

                                    let dummyLeadCard: PIXI.Sprite = new PIXI.Sprite( this._leadCard.faceTexture );
                                    dummyLeadCard.anchor.set( 0.5 );
                                    dummyLeadCard.scale.set( 0.5 );
                                    this._leadCard.addChild( dummyLeadCard );

                                    this._leadCard.setColor( this._leadCardColor );
                                    //
                                    let tl: gsap.core.Timeline = new gsap.core.Timeline();
                                    tl.timeScale( 1.4 );
                                    tl.to( this._leadCard.scale, { x: 1.4, y: 1.4, duration: 0.2, ease: "sine.out" }, 0 );
                                    tl.to( this._leadCard.scale, { x: 1, y: 1, duration: 0.1, ease: "sine.in" }, 0.2 );
                                    tl.call( () => { 
                                        this._wildCardFrameAnimation.position.copyFrom( this._leadCard.position );
                                        this._layers.deck.addChildAt( this._wildCardFrameAnimation, this._layers.deck.children.length - 2 );
                                        this._wildCardFrameAnimation.visible = true;
                                        this._wildCardFrameAnimation.gotoAndPlay( 0 );

                                        this._sparkAnimation.position.copyFrom( this._leadCard.position );
                                        this._layers.deck.addChildAt( this._sparkAnimation, this._layers.deck.children.length - 2 );
                                        this._sparkAnimation.visible = true;
                                        this._sparkAnimation.gotoAndPlay( 0 );

                                        //this._sparkEmitter.playOnce(); 
                                    }, null, 0 );
                                    tl.to( this._leadCard, { angle: this._leadCard.angle + 15, duration: 0.075, ease: "sine.out" }, 0.1 );
                                    tl.to( this._leadCard, { angle: this._leadCard.angle - 15, duration: 0.15, ease: "sine.out" }, ">" );
                                    tl.to( this._leadCard, { angle: this._leadCard.angle, duration: 0.075 * 10, ease: "elastic.out" }, ">" );
                                    tl.to( dummyLeadCard, { alpha: 0, duration: 0.3, ease: "sine.in" }, 0.1 );
                                    tl.call( () => {
                                        ServiceLocator.audio.playSfx( "wild_card" );
                                    }, null, 0.1 );
                                    tl.call( () => { 
                                        dummyLeadCard.parent.removeChild( dummyLeadCard );
                                        dummyLeadCard.destroy();

                                        let targetPlayer: Player = this._findNextPlayer( player, 1 );
                                        this._drawCards( targetPlayer, 4 );
                                        this._drawCardTl.call( () => {
                                            this._nextPlayer( this._findNextPlayer( player, 2 ) );
                                        } );
                                    }, null, 0.4 );
                                    //
                                    ServiceLocator.game.tweenGroup.add( tl );
                                }
                                else if ( card.value == card_value_type.SKIP )
                                {
                                    let skipAnimation: SkipAnimation = new SkipAnimation();
                                    skipAnimation.color = card.color;
                                    skipAnimation.nextPlayer = this._findNextPlayer( player, 1 )
                                    skipAnimation.init();
                                    this._layers.vfx.addChild( skipAnimation );
                                    //
                                    skipAnimation.onCompleted.addOnce( () => {  
                                        skipAnimation.end();
                                        skipAnimation.parent.removeChild( skipAnimation );
                                        skipAnimation.destroy( { children: true } );
                                        //this._specialCardAnimation = null;
                                    } );
                                    skipAnimation.play();
                                    //
                                    //this._specialCardAnimation = skipAnimation;
                                    
                                    this._nextPlayer( this._findNextPlayer( player, 2 ) );
                                }
                                else if ( card.value == card_value_type.REVERSE )
                                {
                                    let reverseAnimation: ReverseAnimation = new ReverseAnimation();
                                    reverseAnimation.spinDir = this._playOrder * -1;
                                    reverseAnimation.color = card.color;                                    
                                    reverseAnimation.init();
                                    this._layers.vfx.addChild( reverseAnimation );
                                    //
                                    reverseAnimation.onReversed.addOnce( () => {
                                        this._playOrderArrowOverlay.texture = ServiceLocator.resources[ "play_order_arrow_" + [ "red", "yellow", "blue", "green" ][ card.color ] ].texture;
                                        this._playOrderArrowOverlay.visible = true;
                                        this._playOrderArrowOverlay.angle = this._playOrderArrow.angle;
                                        this._playOrderArrowOverlay.alpha = 1;

                                        let tl: gsap.core.Timeline = new gsap.core.Timeline();
                                        tl.from( this._playOrderArrowOverlay, { alpha: 0, duration: 0.4, ease: "sine.out" }, 0 );
                                        tl.call( () => {
                                            this._playOrder *= -1;

                                            this._playOrderArrow.angle -= this._playOrder * ( 180 - 30 ); 
                                            this._playOrderArrow.scale.x *= -1;

                                            this._playOrderArrowOverlay.angle = this._playOrderArrow.angle;
                                            this._playOrderArrowOverlay.scale.x = this._playOrderArrow.scale.x;
                                        }, null, 0.4 );
                                        tl.to( this._playOrderArrowOverlay, { alpha: 0, duration: 0.4, ease: "sine.in" }, "<" );
                                        tl.call( () => { this._playOrderArrowOverlay.visible = false; }, null, ">" );
                                        //
                                        ServiceLocator.game.tweenGroup.add( tl );
                                    } );
                                    reverseAnimation.onCompleted.addOnce( () => {  
                                        this._nextPlayer();

                                        this._specialCardAnimation.end();
                                        this._specialCardAnimation.parent.removeChild( this._specialCardAnimation );
                                        this._specialCardAnimation.destroy( { children: true } );
                                        this._specialCardAnimation = null;
                                    } );
                                    reverseAnimation.play();

                                    this._specialCardAnimation = reverseAnimation;
                                }
                                else if ( card.value == card_value_type.DRAW_TWO )
                                {
                                    let targetPlayer: Player = this._findNextPlayer( player, 1 );
                                    let isDrawStack: boolean = false;
                                    for ( let cardAux of targetPlayer.hand )
                                    {
                                        if ( cardAux.value == card_value_type.DRAW_TWO )
                                        {
                                            isDrawStack = true;
                                            break;
                                        } 
                                    }
                                    if ( this._drawStack >= 2 )
                                    {
                                        this._drawStackNotification.setDrawCount( this._drawStack * 2 );
                                        this._drawStackNotification.visible = true;
                                    }

                                    let drawTwoAnimation: DrawTwoAnimation = new DrawTwoAnimation();
                                    drawTwoAnimation.color = card.color;
                                    drawTwoAnimation.nextPlayer = targetPlayer;
                                    drawTwoAnimation.drawCount = this._drawStack * 2;
                                    drawTwoAnimation.init();
                                    this._layers.vfx.addChild( drawTwoAnimation );
                                    //
                                    drawTwoAnimation.onCompleted.addOnce( () => {  
                                        drawTwoAnimation.end();
                                        drawTwoAnimation.parent.removeChild( drawTwoAnimation );
                                        drawTwoAnimation.destroy( { children: true } );
                                        //this._specialCardAnimation = null;
                                    } );
                                    drawTwoAnimation.play();
                                    //
                                    //this._specialCardAnimation = drawTwoAnimation;

                                    if ( isDrawStack )
                                    {
                                        this._drawStack += 1;

                                        this._nextPlayer();
                                    }
                                    else
                                    {
                                        this._drawCards( targetPlayer, this._drawStack * 2 );
                                        this._drawStack = 1;
                                        this._drawStackNotification.visible = false;
                                        this._drawCardTl.call( () => {
                                            this._nextPlayer( this._findNextPlayer( player, 2 ) );
                                        } );
                                    }
                                }
                                else
                                {
                                    this._nextPlayer();
                                }
                            }
                        }

                            private _onGameOverAnimation_Completed(): void
                            {
                                this._gameOverAnimation.end();
                                this._root.removeChild( this._gameOverAnimation );
                                this._gameOverAnimation.destroy( { children: true } );
                                this._gameOverAnimation = null;

                                if ( Session.i.gameType == game_type.MATCHMAKING )
                                {
                                    window.GameSnacks.game.levelComplete( this._matchMakingData.round );

                                    for ( let player of this._arrPlayer )
                                    {
                                        this._matchMakingData.arrRoundScore[ player.id ] = this._findPlayerScore( player );
                                    }

                                    const kArrLastScore: Array<number> = this._matchMakingData.arrTotalScore.slice();

                                    let isGameOver: boolean = ServiceLocator.saved.data.isFirstGame;
                                    if ( !isGameOver )
                                    {
                                        for ( let i: number = 0; i < Session.i.kGameRoomSize; ++i )
                                        {
                                            this._matchMakingData.arrTotalScore[ i ] += this._matchMakingData.arrRoundScore[ i ];
                                            if ( !isGameOver && this._matchMakingData.arrTotalScore[ i ] >= 200 )
                                            {
                                                Session.i.isGameStarted = false;

                                                isGameOver = true;

                                                break;
                                            }
                                        }
                                        // TEST: isGameOver = true;
                                    }
                                    if ( isGameOver )
                                    {
                                        window.GameSnacks.game.gameOver();
                                    }
                                    this._roundOverPopup = new RoundOverPopup();
                                    this._roundOverPopup.playerIndex = this._myPlayer.id;
                                    this._roundOverPopup.isGameOver = isGameOver;
                                    this._roundOverPopup.round = this._matchMakingData.round;
                                    this._roundOverPopup.roundScores = this._matchMakingData.arrRoundScore;
                                    this._roundOverPopup.lastScores = kArrLastScore;
                                    this._roundOverPopup.onEnter();
                                    this._roundOverPopup.onClosed.addOnce( ( popup: RoundOverPopup, isPlay: boolean ) => {
                                        if ( isGameOver )
                                        {
                                            this._root.interactiveChildren = false;

                                            popup.onLeave();

                                            this._onCompleted.dispatch( screen_type.LOBBY, { isPlay: isPlay } );
                                        }
                                        else
                                        {
                                            popup.onLeave();

                                            this._onRoundStarted();
                                        }
                                    } );
                                }
                                else
                                {
                                    window.GameSnacks.game.levelComplete( 1 );

                                    let arrLastScore: Array<number> = [];
                                    let arrRoundScore: Array<number> = [];
                                    for ( let player of this._arrPlayer )
                                    {
                                        arrLastScore.push( 0 );
                                        arrRoundScore.push( this._findPlayerScore( player ) );
                                    }

                                    this._roundOverPopup = new RoundOverPopup();
                                    this._roundOverPopup.playerIndex = this._myPlayer.id;
                                    this._roundOverPopup.isGameOver = true;
                                    this._roundOverPopup.roundScores = arrRoundScore;
                                    this._roundOverPopup.lastScores = arrLastScore;
                                    this._roundOverPopup.onEnter();
                                    if ( this._arrPlayer[ 0 ].isBot )
                                    {
                                        this._roundOverPopup.disablePlayAgainBtn();
                                    }
                                    this._roundOverPopup.onClosed.addOnce( ( popup: RoundOverPopup, isPlayAgain: boolean ) => {
                                        popup.onLeave();

                                        this._roundOverPopup = null;

                                        let isPrivateHost: boolean = !this._arrPlayer[ 0 ].isBot;
                                        if ( isPrivateHost && isPlayAgain )
                                        {
                                            this._isKeepRoom = true;
                                            for ( let missingPlayerId of this._arrMissingPlayerId )
                                            {
                                                if ( missingPlayerId == 0 )
                                                {
                                                    isPrivateHost = false;
                                                    break;
                                                }
                                            }
                                            if ( isPrivateHost )
                                            {
                                                for ( let i: number = Session.i.room.length - 1; i > 0; --i )
                                                {
                                                    for ( let missingPlayerId of this._arrMissingPlayerId )
                                                    {   
                                                        if ( i == missingPlayerId )
                                                        {
                                                            Session.i.room.splice( i, 1 );
                                                            break;
                                                        }
                                                    }
                                                }
                                            }
                                        }
                                        
                                        this._onCompleted.dispatch( screen_type.LOBBY, { isPlayPrivate: isPlayAgain, isPrivateHost: isPrivateHost } );
                                    } );
                                } 
                            }

                            private _onRoundStarted(): void
                            {
                                this._isNextAction = false;

                                this._playOrder = 1;

                                this._playOrderArrow.scale.x = 1;
                                this._playOrderArrow.angle = 0;

                                this._playOrderArrowOverlay.scale.x = 1;
                                this._playOrderArrowOverlay.angle = 0;

                                this._state = state_type.PLAYING;
                                this._drawStack = 1;
                                this._drawStackNotification.visible = false;
                                
                                this._imAliveTimer = 0;
                                this._playerInactivityTimer = 0;

                                this._curPlayer.setHighlighted( false );
                                this._curPlayer = null;

                                this._myPlayer.setHandInteractive( false );
                                this._drawCardTriggerArea.visible = false;
                                this._input.clearSelectedCard();

                                this._layers.shadow.removeChildren();
                                this._layers.shadow.addChild( this._deckShadow );
                
                                for ( let player of this._arrPlayer )
                                {
                                    if ( player.isAway )
                                    {
                                        player.isAway = false;
                                        player.isBot = false;
                                    }
                                }

                                if ( this._playCardTl )
                                {
                                    this._playCardTl.kill();
                                    this._playCardTl = null;
                                }

                                if ( this._drawCardTl )
                                {
                                    this._drawCardTl.kill();
                                    this._drawCardTl = null;
                                }

                                if ( this._actionTextTl )
                                {
                                    this._actionTextTl.kill();
                                    this._actionTextTl = null;
                                }

                                if ( this._drawAlertTl )
                                {
                                    this._drawAlertTl.kill();
                                    this._drawAlertTl = null;

                                    this._hand.visible = false;
                                    this._arrDeckCard[ this._arrDeckCard.length - 1 ].setHighlighted( false );
                                    this._arrDeckCard[ this._arrDeckCard.length - 1 ].filters = [];
                                }

                                if ( this._cardWiggleTl )
                                {
                                    this._cardWiggleTl.kill();
                                    this._cardWiggleTl = null;
                                }

                                this._matchMakingData.round += 1;
                                for ( let i: number = 0; i < Session.i.kGameRoomSize; ++i )
                                {
                                    this._matchMakingData.arrRoundScore[ i ] = 0;
                                }

                                for ( let player of this._arrPlayer )
                                {
                                    player.hand.splice( 0 );
                                }
                                this._arrPlayedCard.splice( 0 );
                                this._arrDeckCard = this._arrCard.slice();
                                Utils.shufflePRNG( this._arrDeckCard );
                                let cardOffset: PIXI.Point = new PIXI.Point();
                                let subDeckCardCount: number = 0;
                                for ( let card of this._arrDeckCard )
                                {
                                    if ( ( card.value == card_value_type.EIGHT || card.value == card_value_type.WILD_DRAW_FOUR ) 
                                        && card.color != card_color_type.ANY )
                                    {
                                        card.setColor( card_color_type.ANY );
                                    }
                                    card.angle = 0;
                                    card.shade.visible = false;
                                    card.setFaceDown();
                                    card.position.set( cardOffset.x, cardOffset.y );
                                    this._deckWrapper.addChild( card );

                                    card.shadow = null;

                                    if ( subDeckCardCount >= 10 )
                                    {
                                        subDeckCardCount = 0;

                                        cardOffset.y -= 6;
                                    }

                                    subDeckCardCount += 1;
                                }

                                for ( let i: number = this._arrDeckCard.length - 1; i >= 0; --i )
                                {
                                    if ( this._arrDeckCard[ i ].value == card_value_type.NINE
                                        || this._arrDeckCard[ i ].value < card_value_type.EIGHT )
                                    {
                                        this._leadCard = this._arrDeckCard.splice( i, 1 )[ 0 ];
                                        break;
                                    }
                                }
                                this._leadCard.position.copyFrom( this._deckWrapper.position );
                                this._layers.deck.addChildAt( this._leadCard, 0 );
                                this._leadCard.setFaceUp();
                                this._leadCard.visible = false;
                                this._leadCardColor = this._leadCard.color;
                                //this._leadCard.position.set( 412, 572 );
                                //this._leadCard.setFaceUp();
                                this._arrPlayedCard.push( this._leadCard );
                                //
                                let leadCardShadow: PIXI.Sprite = new PIXI.Sprite( ServiceLocator.resources.deck_shadow.texture );
                                leadCardShadow.visible = false;
                                leadCardShadow.anchor.set( 0.5 );
                                leadCardShadow.position.copyFrom( this._leadCard.position );
                                leadCardShadow.x -= 10;
                                leadCardShadow.y -= 10;
                                this._layers.shadow.addChild( leadCardShadow );
                                //
                                this._leadCard.shadow = leadCardShadow;

                                //this._dealCards()
                                this._myPlayer.setScore( this._findPlayerScore( this._myPlayer ) );

                                //this._deckShadow.x = this._deckShadow.texture.width * 0.5 + this._kDeckShadowOffsetX;

                                this._playDeckEntryAnimation( false );
                            }

        private _onMultiplayer_EventsReceived( eventsPromise: Promise<LiveEventData[]> ): void
        {
            eventsPromise
                .then( ( arrData: LiveEventData[] ) => {
                    while ( arrData.length > 0  )
                    {
                        let data: LiveEventData = arrData.shift();
                        if ( data.payload !== undefined )
                        {
                            const kPlayerAction: IPlayerAction = JSON.parse( data.payload );
                            if ( kPlayerAction.type == game_event_type.PLAYER_KICKED )
                            {
                                if ( kPlayerAction.playerId == this._myPlayer.id )
                                {
                                    this._onCompleted.dispatch( screen_type.LOBBY, { isKicked: true } );
                                }
                                else
                                {
                                    Session.i.gameEvents.push( data );
                                }
                            }
                            else if ( kPlayerAction.type == game_event_type.CARD_PLAYED
                                || kPlayerAction.type == game_event_type.CARD_DRAWN
                                || kPlayerAction.type == game_event_type.AUTO_PLAY
                                || kPlayerAction.type == game_event_type.IM_ALIVE )
                            {
                                Session.i.gameEvents.push( data );
                            }
                            else if ( kPlayerAction.type == game_event_type.JOINED_PRIVATE_ROOM )
                            {
                                Session.i.roomEvents.push( data );
                            }
                        }
                    }
                } );
        }

        private _onMultiplayer_PlayerPresenceUpdated( presenceEventPromise: Promise<LivePlayerDataEvent> ): void
        {
            presenceEventPromise
                .then( ( livePlayerDataEvent: LivePlayerDataEvent ) => {
                    if ( livePlayerDataEvent.action == LivePresenceAction.LEAVE && !livePlayerDataEvent.isLocalPlayer )
                    {
                        for ( let i: number = 0; i < Session.i.room.length; ++i )
                        {
                            if ( Session.i.room[ i ].uid == livePlayerDataEvent.uid )
                            {
                                if ( i == 0 && this._roundOverPopup )
                                {
                                    this._roundOverPopup.onHostLeft();
                                }

                                if ( !this._arrPlayer[ i ].isBot )
                                {
                                    this._arrMissingPlayerId.push( this._arrPlayer[ i ].id );
                                }
                                break;
                            }
                        }
                    }
                    else if ( livePlayerDataEvent.action == LivePresenceAction.JOIN )
                    {
                        if ( Session.i.room[ 0 ] == Session.i.profile && !Session.i.profile.isFake )
                        {
                            window.GameSnacks.liveMultiplayer.sendEvent( { payload: JSON.stringify( { type: game_event_type.GAME_ALREADY_STARTED, uid: livePlayerDataEvent.uid } ) } );
                        }
                    }
                } );
        }

        private _onProfile_Changed( profile: Profile ): void
        {
            ( Session.i.profile.username as any ) = profile.username;
            ( Session.i.profile.avatar as any ) = profile.avatar;
            if ( Session.i.profile.isFake )
            {
                Session.i.profile.isFake = false;
            } 

            this._myPlayer.name = Session.i.profile.username.prefix;
            this._myPlayer.portraitUrl = Session.i.profile.avatar.src;
            this._myPlayer.updateCharacter();
        }


    public onLeave(): void
    {
        ServiceLocator.multiplayerEventManager.onProfileChange.remove( this._onProfile_Changed, this );

        if ( !Session.i.profile.isFake )
        {
            ServiceLocator.multiplayerEventManager.onEventsReceived.removeAll();
            ServiceLocator.multiplayerEventManager.onPlayerPresenceUpdated.removeAll();
        }

        this._input.onCardSelected.remove( this._onPlayerCard_Selected, this );

        if ( this._playCardTl )
        {
            this._playCardTl.kill();
            this._playCardTl = null;
        }

        if ( this._drawCardTl )
        {
            this._drawCardTl.kill();
            this._drawCardTl = null;
        }

        if ( this._actionTextTl )
        {
            this._actionTextTl.kill();
            this._actionTextTl = null;
        }

        if ( this._drawAlertTl )
        {
            this._drawAlertTl.kill();
            this._drawAlertTl = null;
        }

        if ( this._cardWiggleTl )
        {
            this._cardWiggleTl.kill();
            this._cardWiggleTl = null;
        }

        if ( !this._isKeepRoom && !Session.i.profile.isFake )
        {
            window.GameSnacks.liveMultiplayer.updatePlayerPresence( LivePresenceAction.LEAVE ); 
        }

        if ( this._specialCardAnimation )
        {
            this._specialCardAnimation.end();
            this._specialCardAnimation = null;
        }

        if ( this._gameOverAnimation )
        {
            this._gameOverAnimation.end();
            this._gameOverAnimation = null;
        }

        if ( this._deckCreationAnimation )
        {
            this._deckCreationAnimation.end();
            this._deckCreationAnimation = null;
        }

        if ( this._deckEntryAnimation )
        {
            this._deckEntryAnimation.end();
            this._deckEntryAnimation = null;
        }

        /*this._sparkEmitter.destroy();
        this._sparkEmitter = null;*/

        this._input.end();
        this._input = null;

        for ( let player of this._arrPlayer )
        {
            player.end();
        }
        this._arrPlayer = null;

        if ( this._isKeepRoom )
        {
            Session.i.gameEvents.splice( 0 );
            Session.i.isGameStarted = false;
        }
        else
        {
            Session.i.reset();
        }

        this._settings.end();
        this._settings = null;

        ServiceLocator.game.tweenGroup.reset();

        super.onLeave();
    }

    public onUpdate( dt: number ): void 
    {
        if ( this._isPaused ) { return; }
        if ( this._state == state_type.GAME_OVER ) 
        { 
            if ( Session.i.isLeaveGame )
            {
                Session.i.isLeaveGame = false;
            }

            if ( this._gameOverAnimation )
            {
                this._gameOverAnimation.update( dt );
            }

            if ( this._roundOverPopup )
            {
                this._roundOverPopup.onUpdate( dt );
            }

            return; 
        }

        if ( Session.i.isLeaveGame )
        {
            this._onCompleted.dispatch( screen_type.LOBBY, { isKicked: true } );

            return;
        }

        let m: PIXI.Matrix = new PIXI.Matrix();
        for ( let player of this._arrPlayer )
        {
            for ( let card of player.hand )
            {
                if ( card.shadow )
                {
                    card.shadow.position.copyFrom( this._layers.shadow.toLocal( card.getGlobalPosition() ) );
                    card.shadow.scale.copyFrom( card.scale );
                    card.shadow.angle = card.parent.angle + card.parent.parent.parent.angle;

                    m.identity();
                    m.tx = -10;
                    m.ty = -10;
                    m.rotate( card.shadow.angle * Math.PI / 180 );
                    card.shadow.x += m.tx;
                    card.shadow.y += m.ty;
                }
            }
        }
        for ( let cardLayerChild of this._layers.card.children )
        {
            if ( cardLayerChild instanceof Card && cardLayerChild.shadow )
            {
                cardLayerChild.shadow.position.copyFrom( this._layers.shadow.toLocal( cardLayerChild.getGlobalPosition() ) );
                cardLayerChild.shadow.scale.copyFrom( cardLayerChild.scale );
                cardLayerChild.shadow.angle = cardLayerChild.angle;

                m.identity();
                m.tx = -10;
                m.ty = -10;
                m.rotate( cardLayerChild.shadow.angle * Math.PI / 180 );
                cardLayerChild.shadow.x += m.tx;
                cardLayerChild.shadow.y += m.ty;
            }
        }

        //this._sparkEmitter.update( dt );   
        if ( this._wildCardFrameAnimation )
        {
            this._wildCardFrameAnimation.update( dt );
        }
        if ( this._sparkAnimation )
        {
            this._sparkAnimation.update( dt );
        }
        if ( this._shinyBandShader.enabled )
        {
            this._shinyBandShader.update( dt );
        }
        if ( this._colorSelectionPopup )
        {
            this._colorSelectionPopup.onUpdate( dt );
        }
        if ( this._tentacle.visible )
        {
            this._tentacle.update( dt );
        }
        if ( this._compass.visible )
        {
            this._compass.update( dt );
        }

        this._playOrderArrow.angle += dt * this._playOrder * Math.PI * 6;
        if ( this._playOrderArrowOverlay.visible )
        {
            this._playOrderArrowOverlay.angle = this._playOrderArrow.angle;
        }

        if ( this._isNextAction )
        {
            // Check players leaving during private game.
            if ( Session.i.gameType == game_type.PRIVATE_1_VS_4 
                && this._arrMissingPlayerId.length > 0 )
            {
                let nextPlayer: Player = null;
                if ( this._arrMissingPlayerId.includes( this._curPlayer.id ) )
                {
                    let isNextPlayer: boolean = false;
                    nextPlayer = this._curPlayer;
                    while ( !isNextPlayer )
                    {
                        nextPlayer = this._findNextPlayer( nextPlayer, 1 );
                        isNextPlayer = !this._arrMissingPlayerId.includes( nextPlayer.id );
                    }
                }

                let arrProfile: Array<Profile> = new Array<Profile>();
                let arrPlayer: Array<Player> = new Array<Player>();
                let playerLeftNotificationTl: gsap.core.Timeline = null
                for ( let i: number = 0; i < Session.i.room.length; ++i )
                {
                    if ( this._arrMissingPlayerId.includes( i ) )
                    {
                        let playerLeftNotification: PlayerLeftNotification = new PlayerLeftNotification( Session.i.room[ i ].username.prefix );
                        playerLeftNotification.position.copyFrom( this._layers.vfx.toLocal( this._arrPlayer[ i ].portraitGlobalPos ) );
                        this._layers.vfx.addChild( playerLeftNotification );
                        //
                        playerLeftNotification.scale.set( 0 );
                        playerLeftNotification.alpha = 0;
                        //
                        playerLeftNotificationTl = new gsap.core.Timeline();
                        playerLeftNotificationTl.to( playerLeftNotification.scale, { x: 1, y: 1, duration: 0.4, ease: "back.out" }, 0 );
                        playerLeftNotificationTl.to( playerLeftNotification, { alpha: 1, duration: 0.1, ease: "sine.in" }, 0 );
                        playerLeftNotificationTl.to( playerLeftNotification, { alpha: 0, duration: 0.6, ease: "sine.in" }, 1 );
                        playerLeftNotificationTl.call( () => { 
                            playerLeftNotification.parent.removeChild( playerLeftNotification );
                        }, null );
                        ServiceLocator.game.tweenGroup.add( playerLeftNotificationTl );

                        for ( let card of this._arrPlayer[ i ].hand )
                        {
                            card.position.copyFrom( this._leadCard.position );
                            this._layers.card.addChildAt( card, 0 );
                            this._arrPlayedCard.push( ...this._arrPlayer[ i ].hand );
                        }
                        this._arrPlayer[ i ].end();
                        this._arrPlayer[ i ].parent.removeChild( this._arrPlayer[ i ] );
                    }
                    else
                    {
                        arrProfile.push( Session.i.room[ i ] );

                        arrPlayer.push( this._arrPlayer[ i ] );
                        arrPlayer[ arrPlayer.length - 1 ].id = arrPlayer.length - 1;
                    }
                }
                //
                Session.i.room = arrProfile;
                this._arrPlayer = arrPlayer;
                this._arrMissingPlayerId.splice( 0 );
                
                if ( Session.i.room.length < 3 )
                {
                    this._root.interactiveChildren = false;
                    this._isPaused = true;

                    playerLeftNotificationTl.call( () => {
                        let messagePopup: MessagePopup = new MessagePopup();
                        messagePopup.message = "Not enough players left to continue the game.";
                        messagePopup.isCloseBtn = true;
                        messagePopup.onEnter();
                        messagePopup.onClosed.add( () => {
                            messagePopup.onLeave();

                            this._onCompleted.dispatch( screen_type.LOBBY );
                        } );
                    }, null );

                    return;
                }
                else
                {
                    for ( let i: number = 0; i < this._arrPlayer.length; ++i )
                    {
                        let nextPlayerIndex: number = i + 1;
                        if ( nextPlayerIndex >= this._arrPlayer.length )
                        {
                            nextPlayerIndex = 0;
                        }
                        this._arrPlayer[ i ].nextPlayer = this._arrPlayer[ nextPlayerIndex ];

                        let prevPlayerIndex: number = i - 1;
                        if ( prevPlayerIndex < 0 )
                        {
                            prevPlayerIndex = this._arrPlayer.length - 1;
                        }
                        this._arrPlayer[ i ].prevPlayer = this._arrPlayer[ prevPlayerIndex ];
                    }

                    if ( nextPlayer )
                    {
                        this._curPlayer = nextPlayer;
                        this._onPlayer_TurnAnimCompleted();
                    }
                }
            }

            // Check next action.
            let isAction: boolean = false;

            if ( Session.i.gameEvents.length > 0 )
            {
                const kGameEvent: LiveEventData = Session.i.gameEvents[ 0 ];
                Session.i.gameEvents.shift();
                const kPlayerAction: IPlayerAction = JSON.parse( kGameEvent.payload );
                //console.assert( this._curPlayer.id == kPlayerAction.playerId, "Player action id [" + kPlayerAction.playerId + "] is different from current player id [" + this._curPlayer.id + "]" );
                if ( kPlayerAction.playerId == this._myPlayer.id )
                {
                    isAction = true;
                }
                else if ( kPlayerAction.playerId == this._curPlayer.id )
                {
                    isAction = true;

                    if ( kPlayerAction.type == game_event_type.CARD_PLAYED  )
                    {
                        let playedCard: Card = null;
                        for ( let card of this._arrCard )
                        {
                            if ( card.id == kPlayerAction.cardId )
                            {
                                playedCard = card;
                                break;
                            }
                        }
                        if ( kPlayerAction.selLeadColor !== null )
                        {
                            this._leadCardColor = kPlayerAction.selLeadColor;
                        }
                        this._playCard( playedCard, this._curPlayer );

                        this._isNextAction = false;
                    }
                    else if ( kPlayerAction.type == game_event_type.CARD_DRAWN )
                    {
                        //this._showDrawText();

                        let arrDrawnCard: Array<Card> = this._drawCards( this._curPlayer, 1 );
                        this._drawCardTl.call( ( drawnCard: Card ) => {
                                if ( !this._isValidCard( drawnCard, this._curPlayer ) )
                                {
                                    this._nextPlayer();
                                }
                                else
                                {
                                    this._isNextAction = true;
                                }
                            }, [ arrDrawnCard[ 0 ] ] );

                        this._isNextAction = false;
                    }
                    else if ( kPlayerAction.type == game_event_type.AUTO_PLAY )
                    {
                        if ( !this._arrPlayer[ kPlayerAction.playerId ].isBot )
                        {
                            if ( !this._arrMissingPlayerId.includes( this._curPlayer.id ) )
                            {
                                this._curPlayer.isAway = true;

                                this._arrMissingPlayerId.push( this._curPlayer.id );
                            }
                        }
                    }
                    else if ( kPlayerAction.type == game_event_type.PLAYER_KICKED )
                    {
                        if ( !this._arrPlayer[ kPlayerAction.playerId ].isBot )
                        {
                            if ( !this._arrMissingPlayerId.includes( kPlayerAction.playerId ) )
                            {
                                this._arrMissingPlayerId.push( kPlayerAction.playerId );
                            }
                        }
                    }
                    else if ( kPlayerAction.type == game_event_type.IM_ALIVE )
                    {
                        this._playerInactivityTimer = kPlayerAction.elapsedTime;
                    }
                }
            }

            if ( !isAction )
            {
                if ( Session.i.gameType == game_type.MATCHMAKING )
                {
                    this._updatePlayerInactivityTimer( dt );
                }

                this._manageMissingPlayers();
            }
        }

        if ( this._curPlayer == this._myPlayer 
            && Session.i.gameType == game_type.MATCHMAKING )
        {
            if ( !Session.i.profile.isFake )
            {
                this._imAliveTimer += dt;
                if ( this._imAliveTimer > 4 )
                {
                    this._imAliveTimer = 0;

                    window.GameSnacks.liveMultiplayer.sendEvent( { payload: JSON.stringify( { type: game_event_type.IM_ALIVE, playerId: this._curPlayer.id, elapsedTime: this._playerInactivityTimer } ) } ); 
                }
            }
        }
    }

        private _updatePlayerInactivityTimer( dt: number ): void
        {
            this._playerInactivityTimer += dt;                
            if ( this._playerInactivityTimer > 0 ) // IMPORTANT: 0.1 instead of 0 because TweenGroup is adding timelines with 0.01 delay to ensure activation.
            {
                this._curPlayer.setSpinnerProgress( Math.min( 1, this._playerInactivityTimer/10 ) );
            }

            if ( !this._curPlayer.isBot )
            {
                if ( this._curPlayer == this._myPlayer )
                {
                    // Current player timeout.
                    if ( this._playerInactivityTimer >= 10 )
                    {
                        this._playerInactivityTimer = 0;

                        this._curPlayer.setHandInteractive( false );
                        this._drawCardTriggerArea.visible = false;
                        this._input.clearSelectedCard();

                        if ( !this._arrMissingPlayerId.includes( this._curPlayer.id ) )
                        {
                            this._curPlayer.isAway = true;

                            this._arrMissingPlayerId.push( this._curPlayer.id );

                            if ( !Session.i.profile.isFake )
                            {
                                window.GameSnacks.liveMultiplayer.sendEvent( { payload: JSON.stringify( { type: game_event_type.AUTO_PLAY, playerId: this._curPlayer.id } ) } );
                            }
                        }
                    }
                }
                else
                {
                    // AFK player detection.
                    if ( this._playerInactivityTimer >= 30 )
                    {
                        this._playerInactivityTimer = 0;

                        if ( !this._arrMissingPlayerId.includes( this._curPlayer.id ) )
                        {
                            this._arrMissingPlayerId.push( this._curPlayer.id );

                            if ( !Session.i.profile.isFake )
                            {
                                window.GameSnacks.liveMultiplayer.sendEvent( { payload: JSON.stringify( { type: game_event_type.PLAYER_KICKED, playerId: this._curPlayer.id } ) } );
                            }
                        }
                    }
                }
            }
        }

        private _manageMissingPlayers(): void
        {
            for ( let i: number = 0; i < this._arrMissingPlayerId.length; ++i )
            {
                if ( this._arrMissingPlayerId[ i ] == this._curPlayer.id )
                {
                    this._isNextAction = false;

                    this._arrMissingPlayerId.splice( i, 1 );
                
                    this._curPlayer.isBot = true;

                    if ( this._colorSelectionPopup )
                    {
                        this._colorSelectionPopup.onAutoPlayed();

                        this._colorSelectionPopup.onLeave();
                        this._colorSelectionPopup = null;
                    }
                        
                    this._managePlayerTurn( this._curPlayer ); 
                    break;
                }
            }
        }

    // #endregion //
}

export interface IGameLayers
{
    bg: PIXI.Container;
    shadow: PIXI.Container;
    player: PIXI.Container;
    deck: PIXI.Container;
    card: PIXI.Container;
    vfx: PIXI.Container;
    hud: PIXI.Container;
    top: PIXI.Container;
    inputDisabled: PIXI.Container;
}

interface IPlayerAction
{
    type: game_event_type;
    playerId: number;
    cardId?: number;
    elapsedTime?: number;
    selLeadColor?: card_color_type;
}

interface IMatchMakingGame
{
    round: number;
    arrRoundScore: Array<number>;
    arrTotalScore: Array<number>;
}

const enum state_type
{
    PLAYING = 0,
    GAME_OVER
}